import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router'
import { Api, AppDispatch, GlobalState, useRoutes } from '../../../../store'
import Wrapper from '../../../common/Wrapper'
import Pendable from '../../../common/Pendable'
import { Paper } from '../../../common/Form'
import { equals } from 'common/util'
import { format } from 'date-fns'
import { DATE_FORMAT_LONG } from 'common/api/v1/helpers'
import { clearAdBreak, getAdBreak } from '../../../../redux/actions/adBreakActions'
import { clearAdPod, getAdPod } from '../../../../redux/actions/adPodActions'
import { AdBreak, Role } from 'common/api/v1/types'
import DataSet from '../../../common/DataSet'
import Tooltip from '../../../common/Tooltip'
import { AdBreakStatusIcon, adBreakStatusText } from '../tableConfig'
import { styles } from '../styles'
import Typography from '@mui/material/Typography'
import Stack from '@mui/material/Stack'
import { Link } from '../../../common/Link'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import Button from '@mui/material/Button'
import { getDuration, getDurationFormatted, getEndTimeFormatted, getStartTimeFormatted } from '../utils'
import { EnrichedOutput } from '../../../../api/nm-types'
import { useUser } from '../../../../utils'
import { TrackingSection } from './TrackingSection'
import { AuditSection } from './AuditSection'
import { listAdCreatives } from '../../../../redux/actions/adCreativeActions'
import { getAdServer } from '../../../../redux/actions/adServerActions'
import { splitPlaylistAdId } from 'common/adInsertion'

const { outputApi } = Api

export const ViewAdBreak = () => {
  const { id } = useParams()
  const [output, setOutput] = useState<null | EnrichedOutput>(null)
  const [refreshTimestamp, setRefreshTimestamp] = useState<number>(Date.now())
  const dispatch = useDispatch<AppDispatch>()
  const user = useUser()
  const navigate = useNavigate()

  const { adBreak, adPod, adServer } = useSelector(
    ({ adBreakReducer, adPodReducer, adInsertionReducer }: GlobalState) => ({
      adBreak: adBreakReducer.selectedAdBreak,
      adPod: adPodReducer.selectedAdPod,
      adServer: adInsertionReducer.selectedAdServer,
    }),
    equals,
  )
  const { adCreatives } = useSelector(
    ({ adCreativeReducer }: GlobalState) => ({
      adCreatives: adCreativeReducer.adCreatives,
    }),
    equals,
  )

  const [openVast, setOpenVast] = useState<boolean>(false)
  const routes = useRoutes()

  useEffect(() => {
    if (!id) return

    const fetchAdBreak = () => {
      dispatch(getAdBreak(id))
      setRefreshTimestamp(Date.now())
    }

    fetchAdBreak() // Initial fetch
    const interval = setInterval(fetchAdBreak, 500)

    return () => {
      clearInterval(interval)
      dispatch(clearAdBreak())
    }
  }, [id, dispatch])

  useEffect(() => {
    if (adBreak) {
      dispatch(listAdCreatives({ adBreakId: adBreak.id, pageNumber: '0', rowsPerPage: '1000' }))
    }
  }, [dispatch, adBreak])

  useEffect(() => {
    if (adBreak?.adPodId) {
      dispatch(getAdPod(adBreak.adPodId))
    }

    return () => {
      if (adBreak?.adPodId) {
        dispatch(clearAdPod())
      }
    }
  }, [adBreak?.adPodId, dispatch])

  useEffect(() => {
    const ads = adBreak?.playlist?.ads
    if (ads && ads.length > 0) {
      const splits = splitPlaylistAdId(ads[0].id)
      if (splits) {
        const { adServerId, isBuiltIn } = splits
        if (!isBuiltIn) {
          dispatch(getAdServer(adServerId))
        }
      }
    }
  }, [adBreak?.playlist, dispatch])

  useEffect(() => {
    if (adBreak?.outputId) {
      outputApi
        .getOutput(adBreak.outputId)
        .then((res) => setOutput(res))
        .catch(() => setOutput(null))
    }
  }, [adBreak?.outputId])

  if (!adBreak) {
    return null
  }

  if (user.role !== Role.super && (user.role !== Role.admin || user.group !== adBreak.groupId)) {
    navigate(-1)
    return null
  }

  const dataset = (adBreak: AdBreak) => {
    return {
      Created: format(new Date(adBreak.createdAt), DATE_FORMAT_LONG),
      Status: (
        <Stack direction={'row'} spacing={2} sx={styles.inlineWrapper}>
          <AdBreakStatusIcon status={adBreak.status} />
          <Typography component="div">{adBreakStatusText(adBreak.status, adBreak.errorMessage)}</Typography>
        </Stack>
      ),
      Duration: (() => {
        const { duration } = getDuration(adBreak)
        return (
          <Tooltip title={`${duration ? `${duration} ms` : '-'}`}>
            <span>{getDurationFormatted(adBreak)}</span>
          </Tooltip>
        )
      })(),
      'Event ID': adBreak.eventId,
      'Start Time': getStartTimeFormatted(adBreak),
      'End Time': getEndTimeFormatted(adBreak),
      VAST: (() => {
        if (adBreak.vastXml) {
          return (
            <Typography
              color="secondary.main"
              sx={{
                cursor: 'pointer',
                userSelect: 'none',
              }}
              onClick={() => setOpenVast(true)}
            >
              View XML
            </Typography>
          )
        }

        return '-'
      })(),
      Output: (() => {
        if (output) {
          return (
            <Link to={routes.outputsUpdate({ id: output.id })} underline="hover" available={!!output.id}>
              {output.name}
            </Link>
          )
        }

        return '-'
      })(),
      'Ad Pod': (() => {
        if (adPod) {
          return (
            <Link to={routes.adPodsUpdate({ id: adPod.id })} underline="hover" available={!!adPod.id}>
              {adPod.name}
            </Link>
          )
        }

        return '-'
      })(),
    }
  }

  return (
    <>
      <VastDialog vastXml={adBreak.vastXml} onClose={() => setOpenVast(false)} open={openVast} />
      <Wrapper name={['Ad Insertion', 'Ad Breaks', format(new Date(adBreak.createdAt), DATE_FORMAT_LONG)]}>
        <Pendable pending={!!id && !adBreak}>
          <Paper title={'Metadata'}>
            <DataSet values={dataset(adBreak)} />
          </Paper>
          <TrackingSection adBreak={adBreak} adCreatives={adCreatives} />
          <AuditSection
            dispatch={dispatch}
            adBreak={adBreak}
            adCreatives={adCreatives}
            adPod={adPod}
            adServer={adServer}
            refreshTrigger={refreshTimestamp}
          />
        </Pendable>
      </Wrapper>
    </>
  )
}

const VastDialog = ({ open, onClose, vastXml }: { open: boolean; onClose: () => void; vastXml?: string }) => {
  if (!vastXml) {
    return null
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
      <DialogTitle>VAST XML</DialogTitle>
      <DialogContent dividers>
        <pre
          style={{
            wordBreak: 'break-word',
            fontFamily: 'monospace',
            fontSize: '0.9rem',
            margin: 0,
          }}
        >
          {vastXml}
        </pre>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  )
}
